import React, { useContext, useState, useMemo, useCallback } from "react";
import type { Geography, POI, School } from "@local-logic/client";
import type { MarkerPoint } from "@local-logic/maps";
import {
  type NeighborhoodMapProps,
  type POICategory,
  TrackingPipelineEvents,
  AnalyticsEvents,
} from "./types";
import { MAX_POI_LIMIT } from "./constants";

import { useTracking } from "~/tracking";
import { useGeographies } from "~/features/clientLibrary/useGeographies";
import useEffectOnce from "~/hooks/useEffectOnce";
import { useStateManagerContext } from "~/context/stateManager";
import errorTracker from "~/utils/errorTracker";

import { getIdFromMarker } from "~/utils/map/getIdFromMarker";
import { useMarkers } from "~/hooks/useMarkers";

interface Location {
  lat: number;
  lng: number;
}

export type DefaultContext = {
  locale: "en" | "fr";
  isLoading: boolean;
  map: {
    mapProvider: NeighborhoodMapProps["options"]["mapProvider"];
    zoom: NeighborhoodMapProps["options"]["zoom"];
    pitch: NeighborhoodMapProps["options"]["pitch"];
    bearing: NeighborhoodMapProps["options"]["bearing"];
    cooperativeGestures: NeighborhoodMapProps["options"]["cooperativeGestures"];
  };
  geography?: Geography;
  activeMarker?: MarkerPoint;
  activeMarkerDetails: POI | School | null;
  activePoiCategory?: POICategory;
  onSelectPoiCategory: (category?: POICategory) => void;
  handleMarkerClick: (marker?: MarkerPoint) => void;
  markersData?: POI[] | School[];
  hideAttribution?: boolean;
  showSchoolRatings?: boolean;
};

const NeighborhoodMapContext = React.createContext<DefaultContext>(
  {} as DefaultContext,
);
const useNeighborhoodMap = () => useContext(NeighborhoodMapContext);

const NeighborhoodMapProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { sdkState } = useStateManagerContext<NeighborhoodMapProps>();
  const locale = sdkState.globalOptions?.locale || "en";
  const tracking = useTracking();
  const [activeMarker, setActiveMarker] = useState<MarkerPoint>();
  const [activePoiCategory, setActivePoiCategory] =
    useState<DefaultContext["activePoiCategory"]>();

  useEffectOnce(() => {
    tracking.postEvent({
      eventType: TrackingPipelineEvents.SDK_VIEWED,
      eventAttributes: [
        {
          label: "default_category",
          data: activePoiCategory,
        },
      ],
    });

    tracking.trackAnalyticsEvent({
      eventName: AnalyticsEvents.SDK_VIEWED,
      eventAttributes: {
        ll_default_category: activePoiCategory,
      },
    });
  });

  const { isLoading: isGeographyLoading, data: geographyData } = useGeographies(
    {
      geogID: sdkState.options?.geographyId,
      location: {
        lat: sdkState.options?.lat,
        lng: sdkState.options?.lng,
      },
      include: "geometry",
    },
  );

  const { geographyId, geography } = useMemo(() => {
    const geographiesArray = Object.entries(geographyData || {});

    return {
      geographyId: geographiesArray[0]?.[0],
      geography: geographiesArray[0]?.[1],
    };
  }, [isGeographyLoading, geographyData]);

  const { data: markersDataRaw, isLoading: isMarkersLoading } = useMarkers({
    geogId: geographyId,
    limit: MAX_POI_LIMIT,
    categories: activePoiCategory ? [activePoiCategory] : undefined,
  });

  // Flatten the markers data into a single array
  const markersData = useMemo(
    () =>
      ([] as (POI | School)[]).concat(...Object.values(markersDataRaw || {})),
    [markersDataRaw],
  );

  const handleSelectPoiCategory = (category?: POICategory) => {
    errorTracker.addBreadCrumb(
      "NeighborhoodMapProvider",
      "handleSelectPoiCategory",
      {
        category,
      },
    );

    setActivePoiCategory(activePoiCategory === category ? undefined : category);
  };

  const handleMarkerClick = useCallback(
    (marker?: MarkerPoint) => {
      if (
        activeMarker?.latitude === marker?.latitude &&
        activeMarker?.longitude === marker?.longitude
      ) {
        setActiveMarker(undefined);
        return;
      }

      setActiveMarker(marker);
    },
    [activeMarker],
  );

  const activeMarkerDetails = useMemo(() => {
    if (!markersData || !activeMarker || activeMarker.markers.length !== 1) {
      return null;
    }

    return (
      markersData.find(
        (marker) =>
          activeMarker.markers[0].id ===
          getIdFromMarker(marker.lat, marker.lng, marker.name),
      ) || null
    );
  }, [activeMarker, markersData]);

  const isLoading = useMemo(
    () => isGeographyLoading || isMarkersLoading,
    [isGeographyLoading, isMarkersLoading],
  );

  return (
    <NeighborhoodMapContext.Provider
      value={{
        locale,
        isLoading,
        map: {
          mapProvider: sdkState.options?.mapProvider,
          zoom: sdkState.options?.zoom,
          pitch: sdkState.options?.pitch,
          bearing: sdkState.options?.bearing,
          cooperativeGestures: sdkState.options?.cooperativeGestures,
        },
        geography,
        activeMarker,
        activeMarkerDetails,
        handleMarkerClick,
        activePoiCategory,
        onSelectPoiCategory: handleSelectPoiCategory,
        markersData,
        hideAttribution: sdkState.options?.hideAttribution,
        showSchoolRatings: sdkState.options?.showSchoolRatings,
      }}
    >
      {children}
    </NeighborhoodMapContext.Provider>
  );
};

export { useNeighborhoodMap, NeighborhoodMapProvider, type Location };
